"use client";

import { ClientLogger } from "@/lib/clientLogger";
import { appEvents } from "@/lib/events";

let installed = false;

/**
 * Install global error handlers that forward uncaught errors to ClientLogger.
 * Also monkey-patches console.error and console.warn so browser console output
 * reaches the backend telemetry endpoint.
 *
 * Call once from a top-level client component. Idempotent — multiple calls are safe.
 */
export function installGlobalErrorHandlers(): void {
  if (typeof window === "undefined" || installed) return;
  installed = true;

  window.onerror = (message, source, lineno, colno, error) => {
    ClientLogger.error("Uncaught error", {
      error: error ?? new Error(String(message)),
      component: "global/onerror",
      metadata: { source, lineno, colno, pathname: window.location.pathname },
    });
  };

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const message = reason instanceof Error ? reason.message : String(reason ?? "");

    ClientLogger.error("Unhandled promise rejection", {
      error: reason,
      component: "global/unhandledrejection",
      metadata: { pathname: window.location.pathname },
    });

    // Detect assistant-ui's "Parent message not found" import error and emit
    // a recovery event so ThreadHistoryProvider can force a fresh load.
    if (message.includes("Parent message not found")) {
      appEvents.emit("thread-import-corrupted", { pathname: window.location.pathname });
    }
  });

  // ─── Console monkey-patching ──────────────────────────────────────────────
  // Forward console.error and console.warn to ClientLogger so browser console
  // output (including third-party library errors like assistant-ui) surfaces in
  // the backend telemetry logs. Original functions are preserved — devtools
  // output is unaffected.

  let isForwarding = false; // re-entrancy guard

  const originalError = console.error;
  const originalWarn = console.warn;

  console.error = (...args: unknown[]) => {
    originalError.apply(console, args);
    if (isForwarding) return;
    isForwarding = true;
    try {
      const { message: msg, error } = formatConsoleArgs(args);
      ClientLogger.error(msg, {
        error,
        component: "console/error",
        metadata: getConsoleMetadata(msg, args),
      });
    } finally {
      isForwarding = false;
    }
  };

  console.warn = (...args: unknown[]) => {
    originalWarn.apply(console, args);
    if (isForwarding) return;
    isForwarding = true;
    try {
      const { message: msg, error } = formatConsoleArgs(args);
      ClientLogger.error(msg, {
        error,
        component: "console/warn",
        metadata: getConsoleMetadata(msg, args),
      });
    } finally {
      isForwarding = false;
    }
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatConsoleArgs(args: unknown[]): { message: string; error?: Error } {
  let firstError: Error | undefined;
  const parts: string[] = [];

  for (const arg of args) {
    if (arg instanceof Error) {
      if (!firstError) firstError = arg;
      parts.push(`${arg.message}`);
    } else if (typeof arg === "object" && arg !== null) {
      try {
        parts.push(JSON.stringify(arg));
      } catch {
        parts.push(String(arg));
      }
    } else {
      parts.push(String(arg));
    }
  }

  return { message: parts.join(" ") || "(empty console call)", error: firstError };
}

function getConsoleMetadata(message: string, args: unknown[]): Record<string, unknown> {
  const reactErrorMatch = message.match(/Minified React error #(\d+)/);
  return {
    pathname: window.location.pathname,
    reactErrorCode: reactErrorMatch?.[1] ?? null,
    argTypes: args.map((arg) => arg instanceof Error ? "Error" : typeof arg),
  };
}
